import { useEffect, useState, useMemo } from "react";
import { isFuture, isPast, parseISO } from "date-fns";
import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import {
  statusMessage,
  type StatusMessage as StatusMessageModel,
} from "@/hooks/firestore/models/statusMessage";

import type { Always } from "types/utilities";
import { useInterval } from "usehooks-ts";
import { v4 } from "uuid";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useProfile } from "@/hooks/firestore/user/useProfile";

const filterStatusMessages = <TMessage extends StatusMessageModel>(
  statusMessages: TMessage[] | null | undefined,
): TMessage[] | undefined => {
  if (!statusMessages) return;

  const filteredMessages = statusMessages.filter((message) => {
    const startDate = parseISO(message.scheduledStartTime);
    const endDate = message?.scheduledExpiryTime
      ? parseISO(message.scheduledExpiryTime)
      : null;

    if (isFuture(startDate)) {
      return false;
    }

    if (endDate && isPast(endDate)) {
      return false;
    }

    return true;
  });

  if (filteredMessages.length === 0) return;

  return filteredMessages;
};

const useStatusMessages = () => {
  const isImpersonating = useAuthStore((s) => s.status) === "impersonating";
  const { profile } = useProfile();

  const { data, isLoading, error } = useFirestore(
    "/statusMessages",
    statusMessage.array(),
    {
      keyPath: "id",
    },
  );

  const impersonationStatusMessage = useMemo(() => {
    return isImpersonating && profile
      ? ({
          id: v4(),
          description: `You are currently impersonating ${profile?.username}. Actions taken will affect their account. Proceed with caution.`,
          type: "DANGER",
          scheduledStartTime: new Date().toISOString(),
        } satisfies StatusMessage)
      : null;
  }, [isImpersonating, profile]);

  const statusMessagesWithImpersonation = useMemo(() => {
    if (!impersonationStatusMessage) return data;

    return [...(data || []), impersonationStatusMessage];
  }, [data, impersonationStatusMessage]);

  const [statusMessages, setStatusMessages] = useState<typeof data>();

  useInterval(() => {
    if (!statusMessagesWithImpersonation) return;

    setStatusMessages(filterStatusMessages(statusMessagesWithImpersonation));
  }, 10000);

  useEffect(() => {
    // when server data changes we want to re-calculate the state
    setStatusMessages(filterStatusMessages(statusMessagesWithImpersonation));
  }, [statusMessagesWithImpersonation]);

  return { statusMessages, isLoading, error };
};

type StatusMessage = Always<
  ReturnType<typeof useStatusMessages>["statusMessages"]
>[0];

export { useStatusMessages, type StatusMessage };
